import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@/shared/ui/radix-icons';
import TextInput from './TextInput';
import type { TextInputProps } from './TextInput';
import UnstyledButton from '../../basic/UnstyledButton';
import DatePickerPopover from './DatePickerPopover';

/**
 * Props specific to the DateField wrapper (extends TextInput, forces type="date").
 */
export type DateFieldProps = Omit<TextInputProps, 'type' | 'asTextArea'> & {
  /** Minimum selectable date in ISO format (yyyy-MM-dd). */
  min?: string;
  /** Maximum selectable date in ISO format (yyyy-MM-dd). */
  max?: string;
};

/**
 * Hides the browser's own calendar button on the native input.
 *
 * The native popup renders in the platform's colours and cannot be styled at
 * all, so it was the one part of a form that ignored the design system. The
 * input itself stays `type="date"`, which keeps the locale-aware display, the
 * ISO form value and keyboard typing exactly as they were — only the popup is
 * replaced.
 */
const HIDE_NATIVE_PICKER = [
  // The vendor pseudo-element itself is hidden from base.css: Tailwind does
  // not generate a variant for it, and TextInput puts `className` on its
  // wrapper rather than on the input.
  'ds-date-field',
  // Room for our own trigger, so a long date never runs under it.
  '[&_input]:pr-(--space-9)',
].join(' ');

/**
 * Writes a value into the native input the way the browser would, so
 * react-hook-form's `register` (which listens for real input events) picks it
 * up. Assigning `.value` directly would update the DOM and leave form state
 * behind.
 *
 * @param input - The date input to write to.
 * @param iso - The ISO date, or an empty string to clear.
 */
const commitValue = (input: HTMLInputElement, iso: string): void => {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  )?.set;
  setter?.call(input, iso);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * DS-compliant date input. Keeps the native `<input type="date">` for its
 * value and locale handling, but replaces the browser's unstylable calendar
 * popup with {@link DatePickerPopover}. Forwards ref and supports
 * react-hook-form register spread.
 *
 * @param {DateFieldProps} props - Component props.
 * @returns {React.ReactElement} A date field with the design system's picker.
 */
const DateField = React.forwardRef<HTMLInputElement, DateFieldProps>(
  ({ min, max, className, disabled, readOnly, ...rest }, ref) => {
    const { t } = useTranslation('common');
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const attachRef = useCallback(
      (node: HTMLInputElement | HTMLTextAreaElement | null) => {
        inputRef.current = node as HTMLInputElement | null;
        if (typeof ref === 'function') ref(node as HTMLInputElement | null);
        else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node as HTMLInputElement | null;
        }
      },
      [ref],
    );

    const pick = useCallback((iso: string) => {
      if (inputRef.current) commitValue(inputRef.current, iso);
      setOpen(false);
    }, []);

    return (
      <div className="relative">
        <TextInput
          ref={attachRef}
          type="date"
          min={min}
          max={max}
          disabled={disabled}
          readOnly={readOnly}
          className={[HIDE_NATIVE_PICKER, className].filter(Boolean).join(' ')}
          {...rest}
        />
        {!disabled && !readOnly && (
          <UnstyledButton
            type="button"
            data-testid="date-field-trigger"
            aria-label={t('date_picker.open', 'Abrir calendário')}
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
            className="absolute bottom-(--space-2-5) right-(--space-2-5) flex h-6 w-6 items-center justify-center rounded-(--radius-sm) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
          >
            <CalendarIcon width={14} height={14} />
          </UnstyledButton>
        )}
        {open && (
          <DatePickerPopover
            value={inputRef.current?.value || undefined}
            min={min}
            max={max}
            onSelect={pick}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    );
  },
);

DateField.displayName = 'DateField';
export default DateField;
